import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputValidationIconComponent } from "@components/input-validation-icon/input-validation-icon.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { YesNoOptionsDirective } from "@components/radiobuttongroup/yes-no-options.directive";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbCollapse, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import _, { isNil, sortBy, sum } from "lodash";
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    merge
} from "rxjs";
import {
    filter,
    map,
    scan,
    share,
    takeUntil,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { Kontakter, Produktionsstatus } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUnionType, DateUtils } from "~app/common/utils/date-utils";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VacsKontraktstatus } from "~app/features/virkeskop/models/kontrakt-models";
import { IVKKontraktBetalningDTO } from "~app/features/virkeskop/models/kontraktBetalningar-models";
import { UtskriftTyp } from "~app/features/virkeskop/models/utskrift/utskrift-models";
import { BetalningMediatorService } from "~app/features/virkeskop/services/betalning-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { LeverantorMediatorService } from "~app/features/virkeskop/services/leverantor-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { VirkeskopskontraktLeverantorMediatorService } from "~app/features/virkeskop/services/virkeskopskontrakt-leverantor-mediator.service";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { canEdit } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { ValidationMessageRowComponent } from "../../../../../common/validation/validation-message-row/validation-message-row.component";
import { MaxDateDirective } from "../../../validation/directives/max-date.directive";
import { MinDateDirective } from "../../../validation/directives/min-date.directive";
import { VirkeskopValidationRuleDirective } from "../../../validation/directives/virkeskop-validation-rule.directive";
import { VirkeskopValidationIconPopoverComponent } from "../../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { VirkeskopValidationMessageComponent } from "../../../validation/validation-message/virkeskop-validation-message.component";
import { KontraktGenereraRapportComponent } from "../../kontrakt-utskrift/kontrakt-generera-rapport/kontrakt-generera-rapport.component";
interface BetalningLeverantorVM {
    andelTotal: number;
    betalningTotal: number;
    betalningar: IVKKontraktBetalningDTO[];
    isLeveransvirke: boolean;
    isRotpost: boolean;
    kontraktGuid: string;
    kontraktId: number;
    kontraktsbelopp: number;
    kontraktsbeloppMissing: boolean;
    kontraktsbeloppPercentage: number;
    kvarAttBetala: number;
    leverantor: ILeverantor;
    namn: string;
    resterandeMinDate: DateUnionType;
    resterandeUtbetalning: IVKKontraktBetalningDTO;
    skickadTillVacs: boolean;
    snittnetto: number;
    snittnettoPercentage: number;
    virkesleverantorKontaktGuid: string;
    isVacsAvraknat: boolean;
}

type BetalningLeverantorFormGroup = FormGroup<
    {
  kontraktLeverantor: FormGroup<{
    forskott: FormControl<boolean>;
    betalplan: FormControl<boolean>;
  }>;
  leverantor: FormGroup<{
    persOrgNR: FormControl<string>;
    betalUppgifter: FormControl<string>;
  }>;
  betalning: FormGroup<{
    belopp: FormControl<number>;
    betaldatum: FormControl<string>;
    isKredit: FormControl<boolean>;
  }>;
}>;

@Component({
    selector: "rask-betalning-leverantor",
    templateUrl: "./betalning-leverantor.component.html",
    styleUrls: ["./betalning-leverantor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        NgbCollapse,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        DisabledControlDirective,
        InputValidationIconComponent,
        TextmultilineComponent,
        NgbTooltip,
        RadiobuttonGroupComponent,
        YesNoOptionsDirective,
        CheckboxComponent,
        ValidationMessageRowComponent,
        InputNumberComponent,
        DatepickerComponent,
        MinDateDirective,
        MaxDateDirective,
        ButtonComponent,
        VirkeskopValidationIconPopoverComponent,
        ListComponent,
        ListColumnDirective,
        IconButtonComponent,
        VirkeskopValidationMessageComponent,
        VirkeskopValidationRuleDirective,
        KontraktGenereraRapportComponent,
        AsyncPipe,
        DecimalPipe,
        DatePipe
    ]
})
export class BetalningLeverantorComponent implements OnInit, OnDestroy {
    isForskott? = false;
    skickadTillVacs: boolean;

    protected readonly minDate = DateUtils.shortDateString(DateUtils.now());
    protected readonly negativtBetalningsBeloppSrc = new BehaviorSubject<boolean>(false);

    protected beloppSymbol$: Observable<string>;
    protected createBetalningSrc = new Subject();

    protected form: BetalningLeverantorFormGroup;
    protected isCollapsed = true;
    protected kontraktReadonly = false;
    protected openLeverantorDialogSrc = new Subject<void>();
    protected removeBetalningSrc = new Subject<IVKKontraktBetalningDTO>();
    protected rapportTyp: UtskriftTyp = "Kontrakt-betalplan";
    protected resterandedatum = new FormControl<string>(null);
    protected virkeskopskontraktLeverantorId: number;
    protected vm$: Observable<BetalningLeverantorVM>;

    private readonly betalningMediatorService = inject(BetalningMediatorService);
    private readonly destroy$ = new Subject<void>();
    private readonly fb = inject(FormBuilder);
    private readonly initalState: BetalningLeverantorVM = {
        virkesleverantorKontaktGuid: null,
        kontraktGuid: null,
        kontraktId: 0,
        namn: "",
        betalningTotal: 0,
        kontraktsbeloppPercentage: 0,
        kvarAttBetala: 0,
        kontraktsbelopp: 0,
        snittnettoPercentage: 0,
        snittnetto: 0,
        andelTotal: 0,
        betalningar: [],
        resterandeUtbetalning: null,
        leverantor: null,
        isRotpost: false,
        kontraktsbeloppMissing: false,
        isLeveransvirke: false,
        skickadTillVacs: null,
        isVacsAvraknat: false,
        resterandeMinDate: this.minDate
    };
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);
    private readonly leverantorSrc = new ReplaySubject<ILeverantor>();
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly virkeskopskontraktLeverantorMediatorService = inject(VirkeskopskontraktLeverantorMediatorService);

    private readonly canEdit = canEdit();

    @Input()
    set leverantor(leverantor: ILeverantor) {
        this.leverantorSrc.next(leverantor);
    }

    betalsattIsVisible(): boolean {
        return !this.kontraktMediatorService.kontrakt.isLeveransvirke;
    }

    canCreateBetalplanUtskrift() {
        return this.form.controls.kontraktLeverantor.controls.betalplan.value === true
            && ObjectUtils.hasValue(this.virkeskopskontraktLeverantorId)
            && !this.kontraktMediatorService.kontrakt.isRotpost
            && !this.kontraktMediatorService.kontrakt.isLeveransvirke;
    }

    canEditBetalsatt(): boolean {
        return !this.skickadTillVacs;
    }

    formatBetaluppgift(markagare: ILeverantor): string {
        if (isNil(markagare.betalningsmetod)) {
            return "";
        }

        let ret = `${this.setBetalningsmetodDisplayname(markagare.betalningsmetod)} `;
        switch (markagare.betalningsmetod) {
            case "Konto":
                if (ObjectUtils.stringHasValue(markagare.clearingnummer)) {
                    ret += `${markagare.clearingnummer}-`;
                }
                ret += markagare.bankkonto || "";
                break;
            case "Bankgiro":
                ret += markagare.bankgiro || "";
                break;
            case "Postgrio":
                ret += markagare.plusgiro || "";
                break;
            default:
                break;
        }
        return ret;
    }

    formatMarkagareNamn(markagare: ILeverantor): string {
        let ret = markagare.namn;
        if (markagare.andelTaljare && markagare.andelNamnare) {
            ret += ` - ${markagare.andelTaljare}/${markagare.andelNamnare}`;
        }

        return ret;
    }

    forskottIsVisible(): boolean {
        return this.form.controls.kontraktLeverantor.controls.betalplan.value === false && !this.kontraktMediatorService.kontrakt.isRotpost;
    }

    getFilteredVM = (vm: BetalningLeverantorVM) => {
        const betalnigarTotal = vm.betalningar
            .filter(b => !b.utbetalningResterande)
            .map(b => b.belopp)
            .reduce((a, b) => a + b, 0);
        const andelTotal = this.betalningMediatorService.calcAndelMarkagare(vm.leverantor, vm.snittnetto);
        this.skickadTillVacs = vm.skickadTillVacs;
        let kontraktsbeloppPercentage = 0;
        let snittnettoPercentage = 0;

        if (vm.kontraktsbelopp > 0) {
            kontraktsbeloppPercentage = (betalnigarTotal / vm.kontraktsbelopp) * 100;
        }

        if (andelTotal > 0) {
            snittnettoPercentage = (betalnigarTotal / andelTotal) * 100;
        }
        const allaArbetsorderUtforda = this.kontraktMediatorService.atgarder.every(x => x.arbetsorder?.produktionsStatus === Produktionsstatus.ProduktionGodkand ||
            x.arbetsorder?.produktionsStatus === Produktionsstatus.ProduktionAvslutad ||
            x.arbetsorder?.produktionsStatus === Produktionsstatus.Slutrapporterad);
        const kvarAttBetala = allaArbetsorderUtforda ? 0 : betalnigarTotal - vm.kontraktsbelopp;

        let showBetalplanMissingBetalningValidation = false;
        let showBetalplanGreterThanTenPercentValidation = false;
        const betlaplanValue = this.form.controls.kontraktLeverantor.controls.betalplan.value;
        if (ObjectUtils.hasValue(betlaplanValue) && betlaplanValue &&
            !this.kontraktMediatorService.kontrakt.isLeveransvirke) {
            const sorted = sortBy(vm.betalningar, b => b.utbetalningsdatum);
            const forstaBetalning = this.getForstbetalning(sorted);
            const fortaBetalningPercentage = (forstaBetalning / vm.kontraktsbelopp) * 100;
            showBetalplanMissingBetalningValidation = forstaBetalning < 5000;
            showBetalplanGreterThanTenPercentValidation = fortaBetalningPercentage > 10;
        }

        this.validationService.updateClientSideValidation(showBetalplanMissingBetalningValidation, {
            entityGuids: [vm.leverantor?.guid],
            message: "Vid betalplan Ja ska första delbetalning vara minst 5000 kr.",
            messageId: "betalplanminst5000",
            severity: RuleSeverityLevel.error,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save
        });

        this.validationService.updateClientSideValidation(showBetalplanGreterThanTenPercentValidation, {
            entityGuids: [vm.leverantor?.guid],
            message: "Vid betalplan Ja får betalningar maximalt vara 10% av kontraktsbeloppet.",
            messageId: "betalplanmax10",
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save
        });

        const kontraktsbeloppMissing = (ObjectUtils.isNullOrUndefined(vm.kontraktsbelopp)
            || vm.kontraktsbelopp <= 0) && !vm.isLeveransvirke;

        const betalningar = vm.betalningar.filter(b => !b.utbetalningResterande);
        const betalningarDatum = betalningar.map(x => DateUtils.shortDateString(x.utbetalningsdatum));
        const resterandeMinDate = _.max([
            this.minDate,
            ...betalningarDatum
        ]);

        return {
            ...vm,
            betalningTotal: betalnigarTotal,
            snittnettoPercentage: snittnettoPercentage,
            kontraktsbeloppPercentage: kontraktsbeloppPercentage,
            kvarAttBetala: kvarAttBetala,
            andelTotal: andelTotal,
            betalningar: betalningar,
            kontraktsbeloppMissing,
            resterandeMinDate
        };
    };

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            kontraktLeverantor: this.fb.group({
                forskott: [false],
                betalplan: [null as boolean]
            }),
            leverantor: this.fb.group({
                persOrgNR: [
                    "",
                    [
                        Validators.required,
                        Validators.pattern(Kontakter.PersOrgnummerRegularExpression)
                    ]
                ],
                betalUppgifter: [
                    "",
                    Validators.required
                ]
            }),
            betalning: this.fb.group({
                belopp: [
                    0,
                    [
                        Validators.min(1),
                        Validators.required
                    ]
                ],
                betaldatum: [
                    null as string,
                    [Validators.required]
                ],
                isKredit: [
                    false,
                    Validators.required
                ]
            })
        });

        this.initViewModel();

        this.beloppSymbol$ = this.form.controls.betalning.valueChanges.pipe(
            tap(values => {
                if (values.belopp < 0) {
                    this.form.controls.betalning.patchValue({
                        belopp: 0
                    }, {
                        onlySelf: true,
                        emitEvent: false
                    });
                }
            }),
            map(values => values.isKredit ? "-" : "")
        );

        this.createBetalningSrc.pipe(
            withLatestFrom(this.form.valueChanges, this.form.statusChanges, this.leverantorSrc, this.kontraktMediatorService.kontrakt$),
            filter(([, , status]) => status === "VALID"),
            takeUntil(this.destroy$)
        ).subscribe(([
            , form, , leverantor,
            kontrakt
        ]) => {
            this.betalningMediatorService.addBetalning({
                virkesleverantorKontaktGuid: leverantor.guid,
                guid: GuidUtils.newGuid(),
                belopp: form.betalning.belopp * (form.betalning.isKredit ? -1 : 1),
                utbetalningsdatum: form.betalning.betaldatum
            } as IVKKontraktBetalningDTO);

            this.form.get("betalning").reset({
                belopp: 0,
                betaldatum: null,
                isKredit: false
            });
        });

        this.removeBetalningSrc.pipe(
            takeUntil(this.destroy$)
        ).subscribe(betalning => this.betalningMediatorService.removeBetalning(betalning));

        this.resterandedatum.valueChanges.pipe(
            withLatestFrom(this.leverantorSrc, this.kontraktMediatorService.kontrakt$),
            takeUntil(this.destroy$)
        ).subscribe(([
            datum,
            leverantor,
            kontrakt
        ]) => this.betalningMediatorService.updateResterande({
            virkesleverantorKontaktGuid: leverantor.guid,
            guid: GuidUtils.newGuid(),
            belopp: 0,
            utbetalningsdatum: datum,
            utbetalningResterande: true
        } as IVKKontraktBetalningDTO));

        this.leverantorMediatorService.leverantorChange$.pipe(
            filter(leverantorChange => !isNil(leverantorChange)),
            withLatestFrom(this.vm$),
            filter(([
                leverantorChange,
                vm
            ]) => leverantorChange.leverantor.guid === vm.leverantor.guid),
            takeUntil(this.destroy$)
        ).subscribe(([leverantorChange]) => this.leverantorSrc.next(leverantorChange.leverantor));

        this.openLeverantorDialogSrc
            .pipe(
                withLatestFrom(this.leverantorSrc),
                takeUntil(this.destroy$))
            .subscribe(([, leverantor]) => this.leverantorMediatorService.openRedigeraLeverantorDialog(leverantor));

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.destroy$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);
    }

    openLeverantorDialog(event: Event) {
        event.preventDefault();
        this.openLeverantorDialogSrc.next();
    }

    setBetalningsmetodDisplayname(betalningsmetod: string): string {
        switch (betalningsmetod) {
            case "Konto":
                return "Bankkonto";
            case "Postgiro":
                return "Plusgiro";
            default:
                return betalningsmetod;
        }
    }

    initViewModel() {
        const checkBelopp$ = this.form.controls.betalning.valueChanges.pipe(
            map(form => (vm: BetalningLeverantorVM) => {
                const belopp = form.belopp * (form.isKredit ? -1 : 1);
                const betalningarTotal = sum(vm.betalningar.map(b => b.belopp));
                const isNegative = betalningarTotal + belopp < 0;
                this.negativtBetalningsBeloppSrc.next(isNegative);
                return vm;
            })
        );

        const snittnetto$ = this.betalningMediatorService.snittnetto$.pipe(
            share(),
            map(belopp => (vm: BetalningLeverantorVM) => ({
                ...vm,
                snittnetto: belopp
            })));

        const kontraktsbelopp$ = this.betalningMediatorService.kontraktsbelopp$.pipe(
            share(),
            map(belopp => (vm: BetalningLeverantorVM) => ({
                ...vm,
                kontraktsbelopp: belopp
            })));

        const leverantor$ = this.leverantorSrc.pipe(
            tap(leverantor => {
                this.form.patchValue({
                    leverantor: {
                        persOrgNR: leverantor.persOrgNummer,
                        betalUppgifter: this.formatBetaluppgift(leverantor)
                    }
                }, {
                    onlySelf: true,
                    emitEvent: false
                });
            }),
            map(leverantor => (vm: BetalningLeverantorVM) => ({
                ...vm,
                virkesleverantorKontaktGuid: leverantor.guid,
                namn: this.formatMarkagareNamn(leverantor),
                persOrgNR: leverantor.persOrgNummer,
                betalUppgifter: this.formatBetaluppgift(leverantor),
                leverantor: leverantor
            })));

        const kontrakt$ = this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => (vm: BetalningLeverantorVM) => ({
                ...vm,
                ranta: kontrakt.ranta,
                kontraktId: kontrakt.id,
                kontraktGuid: kontrakt.guid,
                isRotpost: kontrakt.isRotpost,
                skickadTillVacs: ObjectUtils.hasValue(kontrakt.skickadTillVacs),
                isVacsAvraknat: kontrakt.vacsKontraktstatus === VacsKontraktstatus.Avraknat,
                isLeveransvirke: kontrakt.isLeveransvirke
            }
            )));

        const kontraktLeverantorChange$ = this.form.controls.kontraktLeverantor.valueChanges.pipe(
            tap(() => this.resterandedatum.setValue(undefined, {
                onlySelf: true,
                emitEvent: false
            })),
            map(_forskott => (vm: BetalningLeverantorVM) => {
                const betalplan = this.form.controls.kontraktLeverantor.controls.betalplan.value;
                let forskott = this.form.controls.kontraktLeverantor.controls.forskott.value ?? false;
                if (betalplan) {
                    forskott = false;
                }
                this.virkeskopskontraktLeverantorMediatorService.setForskottSrc.next({
                    leverantorGuid: vm.leverantor.guid,
                    kontraktGuid: vm.kontraktGuid,
                    forskott: forskott,
                    betalplan: betalplan,
                    virkeskopsavtalLeverantorId: 0,
                    virkeskopskontraktId: 0
                });
                return vm;
            })
        );

        const forskott$ = this.virkeskopskontraktLeverantorMediatorService.kontraktLeverantorer$.pipe(
            map(forskott => (vm: BetalningLeverantorVM) => {
                const currentForskottInfo = forskott.find(y => y.leverantorId === vm.leverantor.id);
                if (currentForskottInfo) {
                    this.isForskott = currentForskottInfo.forskott ?? false;
                    if (this.isForskott && ObjectUtils.hasValue(this.resterandedatum.value)) {
                        this.resterandedatum.setValue(undefined);
                    }
                    this.virkeskopskontraktLeverantorId = currentForskottInfo.id;
                    this.form.patchValue({
                        kontraktLeverantor: {
                            forskott: currentForskottInfo.forskott ?? false,
                            betalplan: currentForskottInfo.betalplan
                        }
                    }, {
                        onlySelf: true,
                        emitEvent: false
                    });
                }
                return vm;
            })
        );

        const betalningar$ = this.betalningMediatorService.betalningar$.pipe(
            map(betalningar => (vm: BetalningLeverantorVM) => {
                const betalningarForLeverantor = betalningar.filter(b =>
                    b.virkesleverantorKontaktGuid === vm.virkesleverantorKontaktGuid);

                const resterandeUtbetalning = betalningarForLeverantor.find(b => b.utbetalningResterande);
                if (!isNil(resterandeUtbetalning)) {
                    this.resterandedatum.setValue(
                        resterandeUtbetalning.utbetalningsdatum,
                        {
                            onlySelf: true,
                            emitEvent: false
                        }
                    );
                }
                return {
                    ...vm,
                    betalningar: betalningarForLeverantor,
                    resterandeUtbetalning
                };
            }));

        const unfilteredVM$ = merge(
            snittnetto$,
            betalningar$,
            checkBelopp$,
            kontraktsbelopp$,
            leverantor$,
            kontraktLeverantorChange$,
            forskott$,
            kontrakt$)
            .pipe(
                scan((
                    vm: BetalningLeverantorVM,
                    mutatorFn: (vm: BetalningLeverantorVM) => BetalningLeverantorVM) => mutatorFn(vm),
                this.initalState)
            );

        this.vm$ = unfilteredVM$.pipe(
            map(this.getFilteredVM));
    }

    private getForstbetalning(sorted: IVKKontraktBetalningDTO[]): number {
        let forstaBetalning = 0;
        if (ObjectUtils.hasValue(sorted) && sorted.length > 0) {
            const firstDate = DateUtils.shortDateString(sorted[0].utbetalningsdatum);

            const betalningar = new Map<string, number>();
            for (const { utbetalningsdatum, belopp } of sorted) {
                betalningar.set(DateUtils.shortDateString(utbetalningsdatum), (betalningar.get(DateUtils.shortDateString(utbetalningsdatum)) || 0) + belopp);
            }
            forstaBetalning = betalningar.get(firstDate);
        }
        return forstaBetalning;
    }
}
